import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@shared/components/ui/avatar";
import { getFileUrl } from "@shared/utils/fileUrl";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  Package,
  TrendingUp,
  User,
  ArrowRight,
  CheckCircle,
  Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@features/auth/contexts/AuthContext";
import * as pickupService from "@features/pickups/services/pickupService";
import * as packagesService from "@/features/packages/services/packagesService";

const PickupAssignmentSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Fetch all pickups
  const { data: pickupsData } = useQuery({
    queryKey: ["admin-pickups-summary"],
    queryFn: () => pickupService.getAllPickups(),
    select: (data) => data?.pickups || [],
    enabled: user?.role === "admin",
  });

  // Fetch all livreurs
  const { data: livreursData } = useQuery({
    queryKey: ["admin-livreurs-summary"],
    queryFn: () => packagesService.getAllLivreurs(),
    select: (data) => data?.livreurs || [],
    enabled: user?.role === "admin",
  });

  // Only show for admin users
  if (user?.role !== "admin") {
    return null;
  }

  // Calculate assignment statistics
  const assignmentStats =
    pickupsData && livreursData
      ? (() => {
          const livreurAssignments = {};

          // Initialize all livreurs with 0 assignments
          livreursData.forEach((livreur) => {
            livreurAssignments[livreur._id] = {
              ...livreur,
              pickups: 0,
              activePickups: 0,
              completedPickups: 0,
            };
          });

          // Count assignments
          pickupsData.forEach((pickup) => {
            if (pickup.livreur) {
              const livreurId = pickup.livreur._id;
              if (livreurAssignments[livreurId]) {
                livreurAssignments[livreurId].pickups++;

                if (pickup.status === "signed") {
                  livreurAssignments[livreurId].completedPickups++;
                } else if (!["failed", "rejected"].includes(pickup.status)) {
                  livreurAssignments[livreurId].activePickups++;
                }
              }
            }
          });

          const livreurList = Object.values(livreurAssignments);
          const unassignedPickups = pickupsData.filter((p) => !p.livreur);
          const totalAssigned = pickupsData.filter((p) => p.livreur);

          return {
            livreurs: livreurList.sort((a, b) => b.pickups - a.pickups),
            totalLivreurs: livreurList.length,
            activeLivreurs: livreurList.filter((l) => l.activePickups > 0)
              .length,
            unassignedCount: unassignedPickups.length,
            totalAssigned: totalAssigned.length,
            assignmentRate:
              pickupsData.length === 0
                ? 0
                : (totalAssigned.length / pickupsData.length) * 100,
          };
        })()
      : null;

  if (!assignmentStats) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("Pickup Assignments")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t("Pickup Assignments")}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/packages/admin/livreur-management")}
            className="text-muted-foreground hover:text-foreground"
          >
            {t("Manage")}
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Assignment Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {assignmentStats.totalLivreurs}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("Total Livreurs")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {assignmentStats.activeLivreurs}
              </div>
              <div className="text-xs text-muted-foreground">{t("Active")}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {assignmentStats.unassignedCount}
              </div>
              <div className="text-xs text-muted-foreground">
                {t("Unassigned")}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {assignmentStats.assignmentRate.toFixed(0)}%
              </div>
              <div className="text-xs text-muted-foreground">
                {t("Assignment Rate")}
              </div>
            </div>
          </div>

          {/* Top Performing Livreurs */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              {t("Top Performers")}
            </h4>

            {assignmentStats.livreurs.slice(0, 5).map((livreur, index) => (
              <div
                key={livreur._id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={
                          livreur.profileImage
                            ? getFileUrl(livreur.profileImage)
                            : undefined
                        }
                        alt={livreur.fullName}
                      />
                      <AvatarFallback>
                        {livreur.fullName?.charAt(0) ||
                          livreur.email?.charAt(0) ||
                          "L"}
                      </AvatarFallback>
                    </Avatar>
                    {index < 3 && (
                      <div
                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center text-white font-bold ${
                          index === 0
                            ? "bg-yellow-500"
                            : index === 1
                            ? "bg-gray-400"
                            : "bg-orange-500"
                        }`}
                      >
                        {index + 1}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-sm">
                      {livreur.fullName || livreur.email}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {livreur.phone || livreur.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        <Package className="h-3 w-3 mr-1" />
                        {livreur.pickups}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3 text-yellow-500" />
                      <span className="text-xs text-muted-foreground">
                        {livreur.activePickups}
                      </span>
                      <CheckCircle className="h-3 w-3 text-green-500 ml-1" />
                      <span className="text-xs text-muted-foreground">
                        {livreur.completedPickups}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {assignmentStats.livreurs.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t("No livreurs available")}</p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/pickups/management")}
              className="text-xs"
            >
              {t("View Pickups")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/packages/admin/livreur-management")}
              className="text-xs"
            >
              {t("Manage Assignments")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PickupAssignmentSummary;
